import { createContext, useContext } from 'react';

import { DEFAULT_LOCALE, localePath, type Locale } from '../shared/locales.js';
import { getStrings, type Strings } from '../shared/strings.js';

/**
 * The active language, for every component below the root.
 *
 * A context rather than a prop on each component: `Field`, `AmountInput` and `Progress`
 * sit four levels down and would otherwise each need threading, which is exactly the
 * kind of plumbing a translation is most likely to be forgotten in.
 *
 * The default is English, so a component rendered outside a provider (a unit test, for
 * instance) still produces the reference copy rather than throwing.
 */

export const LocaleContext = createContext<Locale>(DEFAULT_LOCALE);

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

/** The dictionary for the active language. */
export function useStrings(): Strings {
  return getStrings(useContext(LocaleContext));
}

/** Build an in-site href in the active language: `/faq` becomes `/es/faq` under Spanish. */
export function useHref(): (path: string) => string {
  const locale = useContext(LocaleContext);
  return (path: string) => localePath(locale, path);
}
